import React from 'react';
import { App, NavGroup, AppRoute } from '@nimbitlabs/layouts';
import { Home, Package, Users, Server, Activity, AlertTriangle, BookOpen, GitBranch, Bell } from 'lucide-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GamificationPanel } from '@/components/GamificationPanel';
import { Dashboard } from '@/pages/Dashboard';
import { registerHydronautLimits } from '@/lib/registerLimits';

// Register limits immediately on module load (before any renders)
registerHydronautLimits();

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});
import { Services } from '@/pages/Services';
import { ServiceDetail } from '@/pages/ServiceDetail';
import { CreateService } from '@/pages/CreateService';
import { EditService } from '@/pages/EditService';
import { Teams } from '@/pages/Teams';
import { TeamDetail } from '@/pages/TeamDetail';
import { CreateTeam } from '@/pages/CreateTeam';
import { EditTeam } from '@/pages/EditTeam';
import { Environments } from '@/pages/Environments';
import { EnvironmentDetail } from '@/pages/EnvironmentDetail';
import { CreateEnvironment } from '@/pages/CreateEnvironment';
import { EditEnvironment } from '@/pages/EditEnvironment';
import { Deployments } from '@/pages/Deployments';
import { ServiceHealthDashboard } from '@/pages/ServiceHealthDashboard';
import { ServiceMonitoring } from '@/pages/ServiceMonitoring';
import Alerts from '@/pages/Alerts';
import AlertRules from '@/pages/AlertRules';
import DependencyGraph from '@/pages/DependencyGraph';
import Runbooks from '@/pages/Runbooks';
import { CreateRunbook } from '@/pages/CreateRunbook';
import { EditRunbook } from '@/pages/EditRunbook';
import Incidents from '@/pages/Incidents';
import Landing from '@/components/landing/LandingPage';
import { ProtectedPermissionRoute } from '@nimbitlabs/auth';
import { Upgrade } from '@nimbitlabs/layouts';

// Define navigation structure
const navItems: NavGroup[] = [
  {
    title: 'Core',
    items: [
      { title: 'Dashboard', url: '/dashboard', icon: Home },
      { title: 'Services', url: '/services', icon: Package, permission: 'services:read' },
      { title: 'Teams', url: '/teams', icon: Users, permission: 'teams:read' },
      { title: 'Environments', url: '/environments', icon: Server, permission: 'environments:read' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { title: 'Deployments', url: '/deployments', icon: GitBranch, permission: 'deployments:read' },
      { title: 'Incidents', url: '/incidents', icon: AlertTriangle, permission: 'incidents:read', requirePaidPlan: true },
      { title: 'Alerts', url: '/alerts', icon: Bell, permission: 'alerts:read' },
      { title: 'Monitoring', url: '/monitoring', icon: Activity, permission: 'monitoring:read', requirePaidPlan: true },
    ],
  },
  {
    title: 'Resources',
    items: [
      { title: 'Runbooks', url: '/runbooks', icon: BookOpen, permission: 'runbooks:read' },
      { title: 'Dependencies', url: '/dependencies', icon: GitBranch, permission: 'dependencies:read' },
    ],
  },
];

// Define all routes with permission-based protection
const routes: AppRoute[] = [
  // Dashboard (no permission required)
  { path: '/dashboard', element: <Dashboard /> },
  
  // Services
  { 
    path: '/services', 
    element: <ProtectedPermissionRoute element={<Services />} permission="services:read" />, 
    permission: 'services:read' 
  },
  { 
    path: '/services/new', 
    element: <ProtectedPermissionRoute element={<CreateService />} permission="services:create" />, 
    permission: 'services:create' 
  },
  { 
    path: '/services/:id', 
    element: <ProtectedPermissionRoute element={<ServiceDetail />} permission="services:read" />, 
    permission: 'services:read' 
  },
  { 
    path: '/services/:id/edit', 
    element: <ProtectedPermissionRoute element={<EditService />} permission="services:update" />, 
    permission: 'services:update' 
  },
  
  // Teams
  { 
    path: '/teams', 
    element: <ProtectedPermissionRoute element={<Teams />} permission="teams:read" />, 
    permission: 'teams:read' 
  },
  { 
    path: '/teams/new', 
    element: <ProtectedPermissionRoute element={<CreateTeam />} permission="teams:create" />, 
    permission: 'teams:create' 
  },
  { 
    path: '/teams/:id', 
    element: <ProtectedPermissionRoute element={<TeamDetail />} permission="teams:read" />, 
    permission: 'teams:read' 
  },
  { 
    path: '/teams/:id/edit', 
    element: <ProtectedPermissionRoute element={<EditTeam />} permission="teams:update" />, 
    permission: 'teams:update' 
  },
  
  // Environments
  { 
    path: '/environments', 
    element: <ProtectedPermissionRoute element={<Environments />} permission="environments:read" />, 
    permission: 'environments:read' 
  },
  { 
    path: '/environments/new', 
    element: <ProtectedPermissionRoute element={<CreateEnvironment />} permission="environments:create" />, 
    permission: 'environments:create' 
  },
  { 
    path: '/environments/:id', 
    element: <ProtectedPermissionRoute element={<EnvironmentDetail />} permission="environments:read" />, 
    permission: 'environments:read' 
  },
  { 
    path: '/environments/:id/edit', 
    element: <ProtectedPermissionRoute element={<EditEnvironment />} permission="environments:update" />, 
    permission: 'environments:update' 
  },
  
  // Operations
  { 
    path: '/deployments', 
    element: <ProtectedPermissionRoute element={<Deployments />} permission="deployments:read" />, 
    permission: 'deployments:read' 
  },
  { 
    path: '/incidents', 
    element: <ProtectedPermissionRoute element={<Incidents />} permission="incidents:read" requirePaidPlan={true} />, 
    permission: 'incidents:read', 
    requirePaidPlan: true 
  },
  { 
    path: '/alerts', 
    element: <ProtectedPermissionRoute element={<Alerts />} permission="alerts:read" />, 
    permission: 'alerts:read' 
  },
  { 
    path: '/alerts/rules', 
    element: <ProtectedPermissionRoute element={<AlertRules />} permission="alerts:manage" />, 
    permission: 'alerts:manage' 
  },
  { 
    path: '/monitoring', 
    element: <ProtectedPermissionRoute element={<ServiceMonitoring />} permission="monitoring:read" requirePaidPlan={true} />, 
    permission: 'monitoring:read', 
    requirePaidPlan: true 
  },
  { 
    path: '/service-health', 
    element: <ProtectedPermissionRoute element={<ServiceHealthDashboard />} permission="monitoring:read" />, 
    permission: 'monitoring:read' 
  },
  
  // Resources
  { 
    path: '/runbooks', 
    element: <ProtectedPermissionRoute element={<Runbooks />} permission="runbooks:read" />, 
    permission: 'runbooks:read' 
  },
  { 
    path: '/runbooks/new', 
    element: <ProtectedPermissionRoute element={<CreateRunbook />} permission="runbooks:create" />, 
    permission: 'runbooks:create' 
  },
  { 
    path: '/runbooks/:id/edit', 
    element: <ProtectedPermissionRoute element={<EditRunbook />} permission="runbooks:update" />, 
    permission: 'runbooks:update' 
  },
  { 
    path: '/dependencies', 
    element: <ProtectedPermissionRoute element={<DependencyGraph />} permission="dependencies:read" />, 
    permission: 'dependencies:read' 
  },
  
  // Override shared /upgrade route with app-specific features
  { 
    path: '/upgrade', 
    element: <Upgrade 
      features={[
        'Advanced incident management',
        'Real-time service monitoring',
        'Custom alert rules and notifications',
        'Automated runbook execution',
        'Full audit trail and compliance',
        'Unlimited services and environments',
      ]}
    /> 
  },
];


function AppContent() {
  return null;
}

export default function HydronautApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <App
        appName="Hydranaut"
        appNavItems={navItems}
        routes={routes}
        landingPage={<Landing appName="Hydranaut" />}
        showManagementItems={true}
        apiBaseUrl={import.meta.env.VITE_HYDRANAUT_API_URL || import.meta.env.VITE_API_URL || 'https://api.hydranaut.io'}
        contentRibbon={
          <>
            <AppContent />
            <GamificationPanel />
          </>
        }
      />
    </QueryClientProvider>
  );
}
